import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CoordinateSystemsLookupService } from '@app/core/coordinate-systems/services/coordinate-systems-lookup.service';
import { ldCoordinatesFieldLabel } from '@app/main/leak-detection/configs/ld-coordinates.config';
import { LabelValuePresentationComponent } from '@app/shared/components/label-value-presentation/label-value-presentation.component';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { NgxPermissionsModule } from 'ngx-permissions';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { CommentCardComponent } from '@app/shared/components/comment/comment-card/comment-card.component';
import { FpLoggerNoiseSummaryStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-summary/services/fp-logger-noise-summary/fp-logger-noise-summary.store';
import { FpLoggerNoiseTabs } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/enums/fp-logger-noise-tabs.enum';
import { LeakDetectionMode } from '@app/core/enums/leak-detection-mode.enum';
import { LdLoggerNoiseTypeIconWithScoreComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-type-icon-with-score/ld-logger-noise-type-icon-with-score.component';
import { LdLoggerNoiseSoundSignalLoadComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-sound-signal-load/ld-logger-noise-sound-signal-load.component';
import { LdLoggerNoiseEnvNoiseEditComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-env-noise-edit/ld-logger-noise-env-noise-edit.component';
import { LdLoggerNoiseEclIndicatorComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-ecl-indicator/ld-logger-noise-ecl-indicator.component';
import { LdLoggerNoiseServingCellIdComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-serving-cell-id/ld-logger-noise-serving-cell-id.component';
import { CommentCardNoDataComponent } from '@app/shared/components/comment/comment-card-no-data/comment-card-no-data.component';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';

@Component({
  selector: 'gc-fp-logger-noise-summary',
  standalone: true,
  imports: [
    TranslatePipe,
    LabelValuePresentationComponent,
    ElementWidthClassDirective,
    NgxPermissionsModule,
    CommentCardComponent,
    LdLoggerNoiseTypeIconWithScoreComponent,
    LdLoggerNoiseSoundSignalLoadComponent,
    LdLoggerNoiseEnvNoiseEditComponent,
    LdLoggerNoiseEclIndicatorComponent,
    LdLoggerNoiseServingCellIdComponent,
    CommentCardNoDataComponent,
  ],
  templateUrl: './fp-logger-noise-summary.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FpLoggerNoiseSummaryComponent {
  public readonly currentTabStore = inject(FpLoggerNoiseSummaryStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly formattingData = inject(FormattingDataService);
  protected readonly coordinateSystems = inject(CoordinateSystemsLookupService);
  protected readonly floatingPopupService = inject(FloatingPopupService);
  protected readonly floatingPopupTabsService = inject(FloatingPopupTabsService);

  protected readonly PermissionName = PermissionName;

  public popup = input<IFloatingPopupInstance>();

  public readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public readonly deployment = computed(() => this.data()?.loggerDeployment);
  public readonly measurement = computed(() => this.data()?.loggerMeasurement);

  // Header
  public readonly loggerType = computed(() => this.deployment()?.loggerType);
  public readonly leakScore = computed(() => this.measurement()?.leakScore);
  public readonly leakScoreStatus = computed(() => this.measurement()?.leakScoreStatus);
  public readonly leakDetectionMode = toSignal(this.currentTabStore.leakDetectionMode$, {
    initialValue: LeakDetectionMode.LeakScore,
  });

  // Summary fields
  public readonly soundSignalId = computed(() => this.measurement()?.soundSignalId ?? null);
  public readonly soundSignalUrl = computed(() => this.measurement()?.soundSignalUrl ?? null);
  public readonly serialNo = computed(() => this.formattingData.formatSerialNo(this.deployment()?.serialNo));
  public readonly rawSerialNo = computed(() => this.deployment()?.serialNo ?? '');
  public readonly lowestNoiseDecibel = computed(() =>
    this.formattingData.formatDecibel(this.measurement()?.lowestNoiseDecibel)
  );
  public readonly spread = computed(() => this.formattingData.formatDecibel(this.measurement()?.spread));
  public readonly measStartTime = computed(() =>
    this.formattingData.formatTime(this.measurement()?.measStartTime, DayJSDateFormat.HHmm)
  );
  public readonly soundSignalStartAt = computed(() =>
    this.formattingData.formatTime(this.measurement()?.soundSignalStartAt)
  );
  public readonly location = computed(() => this.deployment()?.location ?? this.placeholder);
  public readonly latitudeLongitude = computed(() =>
    this.formattingData.formatCoordinates(this.deployment()?.coordinates)
  );
  public readonly coordinates = computed(() =>
    this.formattingData.formatCustomCoordinates(this.deployment()?.customCoordinates)
  );

  public readonly coordinatesLabel = computed(() =>
    ldCoordinatesFieldLabel(this.coordinateSystems.getFor(this.deployment()?.customCoordinates))
  );
  public readonly servingCellId = computed(() => this.deployment()?.servingCellId);
  public readonly envNoiseValue = computed(() => this.measurement()?.envNoiseDecibel);

  public readonly commentAuthor = computed(() => this.deployment()?.comment?.author || this.placeholder);
  public readonly commentModifiedAt = computed(() => {
    const modifiedAt = this.deployment()?.comment?.modifiedAt;
    if (!modifiedAt) {
      return this.placeholder;
    }

    return this.formattingData.formatDate(modifiedAt);
  });
  public readonly comment = computed(() => this.deployment()?.comment?.content || this.placeholder);

  // Developer-only fields
  public readonly eclValue = computed(() => this.deployment()?.eclForMeasurement);
  public readonly channelAndCell = computed(() => this.measurement()?.channelAndCell ?? this.placeholder);
  public readonly roundTripTime = computed(() => this.deployment()?.roundTripTime ?? this.placeholder);
  public readonly bandUsed = computed(() => this.deployment()?.usedBandForMeasurement ?? this.placeholder);
  public readonly scPci = computed(() => this.deployment()?.scPci ?? this.placeholder);
  public readonly scPciState = computed(() => this.deployment()?.scPciState ?? this.placeholder);
  public readonly cellTime = computed(() => this.measurement()?.cellTime ?? this.placeholder);
  public readonly syncTime = computed(() => this.measurement()?.syncTime ?? this.placeholder);
  public readonly connectionTime = computed(() => this.measurement()?.connectionTime ?? this.placeholder);
  public readonly signalType = computed(() => this.measurement()?.soundSignalType ?? this.placeholder);
  public readonly soundSignalSamplingFrequency = computed(
    () => this.measurement()?.soundSignalSamplingFrequency ?? this.placeholder
  );
  public readonly ls1_4 = computed(() => this.measurement()?.ls1_4 ?? this.placeholder);
  public readonly dBover = computed(() => this.measurement()?.dBover ?? this.placeholder);
  public readonly fRated1_4 = computed(() => this.measurement()?.fRated1_4 ?? this.placeholder);
  public readonly fPeaks1_4 = computed(() => this.measurement()?.fPeaks1_4 ?? this.placeholder);
  public readonly dyn1_4 = computed(() => this.measurement()?.dyn1_4 ?? this.placeholder);
  public readonly periodicity = computed(() => this.measurement()?.periodicity ?? this.placeholder);
  public readonly facCorrels = computed(() => this.measurement()?.facCorrels ?? this.placeholder);
  public readonly l1_1_4 = computed(() => this.measurement()?.l1_1_4 ?? this.placeholder);
  public readonly l3_1_4 = computed(() => this.measurement()?.l3_1_4 ?? this.placeholder);
  public readonly l4_1_4 = computed(() => this.measurement()?.l4_1_4 ?? this.placeholder);
  public readonly l5_1_4 = computed(() => this.measurement()?.l5_1_4 ?? this.placeholder);
  public readonly l6_1_4 = computed(() => this.measurement()?.l6_1_4 ?? this.placeholder);
  public readonly l7_1_4 = computed(() => this.measurement()?.l7_1_4 ?? this.placeholder);
  public readonly ccount = computed(() => this.measurement()?.ccount ?? this.placeholder);
  public readonly spectrumStdDev = computed(() => this.measurement()?.spectrumStdDev ?? this.placeholder);

  private get placeholder(): string {
    return this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY;
  }

  public changeToCommentTab() {
    const popup = this.popup();

    if (popup) {
      if (popup.isMinimized) {
        this.floatingPopupService.maximize(popup.id);
      }

      this.floatingPopupTabsService.setActive(popup.id, FpLoggerNoiseTabs.Comments);
    }
  }
}
